import { Box, Flex, Text, Badge, Icon, LightMode } from '@chakra-ui/react';
import LineChart from 'components/charts/LineChart';
import { BsArrowsAngleExpand } from 'react-icons/bs';
import activosApi from 'api/activos';
import { useEffect, useState } from 'react';
import { lineChartOptionsSidebar } from 'variables/charts';

const hotelIdMap: Record<string, number> = {
  GVRN: 1,
  GVRM: 2,
  GVBLC: 3,
  GVLC: 4,
  VV: 5,
  CV: 6,
  MDC: 7,
  CC: 8,
  DEMO: 9,
};

const getLast6Months = () => {
  const now = new Date();
  const months: { key: string; label: string }[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('es-MX', { month: 'short' });
    months.push({ key, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return months;
};

const parseActivoDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d;
  // Formato es-MX: "24/06/2026, 10:30:00 ..."
  const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (match) {
    return new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1]));
  }
  return null;
};

export default function SidebarDocs(props: { mini: boolean; hovered: boolean }) {
  const { mini, hovered } = props;

  const [totalActivos, setTotalActivos] = useState(0);
  const [chartData, setChartData] = useState<{ name: string; data: number[] }[]>([]);
  const [chartOptions, setChartOptions] = useState(lineChartOptionsSidebar);
  const [hotelCode, setHotelCode] = useState(
    (sessionStorage.getItem('hotelSeleccionado') || '').toUpperCase()
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchListado = async () => {
      setIsLoading(true);
      try {
        const hotelId = hotelIdMap[hotelCode];

        const listado: any[] = hotelId
          ? await activosApi.listadoPorPropiedad(hotelId)
          : await activosApi.listado();

        setTotalActivos(listado.length);

        // Agrupar por mes (últimos 6 meses)
        const months = getLast6Months();
        const countByKey: Record<string, number> = {};
        months.forEach(m => (countByKey[m.key] = 0));

        listado.forEach((activo: any) => {
          const date = parseActivoDate(activo.CreatedDate);
          if (!date) return;
          const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (key in countByKey) countByKey[key]++;
        });

        setChartData([{ name: 'Activos', data: months.map(m => countByKey[m.key]) }]);
        setChartOptions({
          ...lineChartOptionsSidebar,
          xaxis: {
            ...lineChartOptionsSidebar.xaxis,
            categories: months.map(m => m.label),
          },
        });
      } catch (error) {
        console.error('Error obteniendo listado de activos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchListado();
  }, [hotelCode]);

  useEffect(() => {
    const onHotelChange = (e: Event) => {
      const hotel = (e as CustomEvent<{ hotel: string }>).detail.hotel.toUpperCase();
      setHotelCode(hotel);
    };

    window.addEventListener('hotelCambiado', onHotelChange);
    return () => window.removeEventListener('hotelCambiado', onHotelChange);
  }, []);

  const visible =
    mini === false ? 'block' : mini === true && hovered === true ? 'block' : 'none';

  return (
    <Flex
      justify="center"
      direction="column"
      align="center"
      bgGradient="linear(to-b, brand.400, brand.600)"
      borderRadius="20px"
      me="20px"
      position="relative"
    >
      <Icon
        display={mini === true && hovered === false ? 'block' : 'none'}
        h="26px"
        w="26px"
        my="100px"
        mx="20px"
        color="white"
        as={BsArrowsAngleExpand}
      />
      <Flex
        direction="column"
        mb="12px"
        align="center"
        justify="center"
        px="15px"
        pt="30px"
        display={visible}
        style={{
          opacity: isLoading ? 0 : 1,
          transform: isLoading ? 'scale(0.96)' : 'scale(1)',
          transition: 'opacity 0.35s ease, transform 0.35s ease',
        }}
      >
        <Text
          display={visible}
          fontSize={{ base: 'lg', xl: '2xl' }}
          color="white"
          fontWeight="bold"
          lineHeight="150%"
          textAlign="center"
          px="10px"
        >
          {totalActivos.toLocaleString()}
        </Text>
        <Text
          display={visible}
          fontSize="sm"
          color="white"
          px="10px"
          mb="14px"
          textAlign="center"
        >
          Cantidad de Activos
        </Text>
        <LightMode>
          <Badge
            display={visible}
            colorScheme="green"
            color="green.500"
            size="lg"
            maxW="max-content"
            mx="auto"
            borderRadius="58px"
          >
            Últimos 6 meses
          </Badge>
        </LightMode>
        <Box h="160px" display={visible}>
          {chartData.length > 0 && (
            <LineChart chartData={chartData} chartOptions={chartOptions} />
          )}
        </Box>
      </Flex>
    </Flex>
  );
}
